// ─────────────────────────────────────────────────────────────────────────────
// THE CRASH SCREEN — what "Something went wrong" says when it is not enough.
//
// ⚠️ WRITTEN BECAUSE A REPRODUCIBLE BUG COULD NOT BE REPRODUCED. Opening a
// ticket crashed for a real user, repeatedly, while the page rendered cleanly
// against every payload the API can actually produce — realistic tickets,
// external tickets, every message kind, missing endpoints, and the live rows
// from the database. Each round of diagnosis cost a message to the user and
// returned the same sentence: "Something went wrong."
//
// The screen knew the error the whole time and threw it away. Sentry had it,
// but reading Sentry is not something the person in front of the broken screen
// can do, and the trace is useless to whoever is fixing it if nobody can quote
// it.
//
// ⚠️ THE DETAIL IS FOLDED AWAY, NOT ABSENT. A stack trace as the front door
// tells a borrower their mortgage application broke in a way that sounds like
// their fault. Collapsed, the reassuring sentence still leads — and one tap
// produces something worth pasting into a message.
//
// ⚠️ AND IT NAMES THE BUILD. "It still happens" and "the fix is deployed" were
// both true at once this week, because they were about different bundles.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { buildLine, COMMIT_SHA, BUILD_SOURCE } from '../constants/build';

const wrap = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif',
  color: '#111827', padding: 24, textAlign: 'center',
};

export default function CrashScreen({ error, componentStack, eventId }) {
  const [copied, setCopied] = useState(false);

  // The component stack is long and mostly noise; the first frames name the
  // route and the component that actually threw, which is the whole question.
  const frames = String(componentStack || '')
    .split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 8);

  const report = [
    `error:  ${error?.message || String(error) || 'unknown'}`,
    `where:  ${window.location.pathname}${window.location.search}`,
    `build:  ${COMMIT_SHA} (${BUILD_SOURCE})`,
    eventId ? `sentry: ${eventId}` : null,
    frames.length ? `stack:\n  ${frames.join('\n  ')}` : null,
  ].filter(Boolean).join('\n');

  const copy = () => {
    // ⚠️ A <textarea> FALLBACK, because navigator.clipboard is unavailable on
    // insecure origins and in some WKWebView contexts — the exact places this
    // screen is most likely to be seen.
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 4000); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(report).then(done).catch(() => {});
      return;
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = report; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); document.execCommand('copy');
      document.body.removeChild(ta); done();
    } catch (_) { /* leave the text on screen to select by hand */ }
  };

  return (
    <div style={wrap}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</div>
      <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24, maxWidth: 400, lineHeight: 1.6 }}>
        Our team has been notified. Please refresh and try again.
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{ background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        Refresh Page
      </button>

      <details style={{ marginTop: 24, maxWidth: 560, width: '100%', textAlign: 'left' }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
          Technical details
        </summary>
        <pre style={{
          marginTop: 10, padding: 12, background: '#f9fafb', border: '1px solid #e5e7eb',
          borderRadius: 8, fontSize: 11, lineHeight: 1.6, color: '#374151',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto',
        }}>{report}</pre>
        <button onClick={copy}
          style={{ marginTop: 8, background: '#fff', color: '#3a6ad9', border: '1px solid #dbe3f7',
            borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          {copied ? '✓ Copied' : 'Copy details'}
        </button>
        <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af' }}>
          Sending this with your message is what makes it fixable.
        </div>
      </details>

      <div style={{ marginTop: 16, fontSize: 12, color: '#9ca3af' }}>
        If this keeps happening, email{' '}
        <a href="mailto:support@godoorly.com" style={{ color: '#3a6ad9' }}>support@godoorly.com</a>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#d1d5db', fontFamily: 'ui-monospace, monospace' }}>
        {buildLine()}
      </div>
    </div>
  );
}
